import Box from "ds/components/Box";
import PageInfoHeader from "ds/components/PageInfoHeader";
import Select from "ds/components/Select";
import CSVDownloadDrawerButton from "views/Account/Settings/Usage/CSVDownload/DrawerButton";
import { getCSVDownloadRange } from "views/Account/Settings/Usage/CSVDownload/utils";
import UsageLayoutHeader from "views/Account/Settings/Usage/Layout/Header";
import { formatUsageDateRange } from "views/Account/Settings/Usage/utils";

import { WorkerUsageData, WorkerUsageSelectableCycle } from "../types";
import {
  getWorkerUsageCycleDay,
  getWorkerUsageCycleTotalDays,
  hasWorkerUsageContract,
} from "../utils";
import styles from "./styles.module.css";

type UsageWorkersHeaderProps = {
  usage?: WorkerUsageData;
  cyclesAgo: number;
  onCycleChange: (cyclesAgo: number) => void;
};

const getCycleLabel = (usage: WorkerUsageData) =>
  hasWorkerUsageContract(usage) ? "Billing cycle" : "Cycle";

const buildCycleCaption = (usage: WorkerUsageData, cycle: WorkerUsageSelectableCycle) => {
  const range = `${getCycleLabel(usage)}: ${formatUsageDateRange(cycle.start, cycle.end)}`;

  if (cycle.cyclesAgo > 0) {
    return `${range} · cycle complete`;
  }

  return `${range} · day ${getWorkerUsageCycleDay(usage)} of ${getWorkerUsageCycleTotalDays(usage)}`;
};

const getCycleOption = (cycle: WorkerUsageSelectableCycle) => {
  const range = formatUsageDateRange(cycle.start, cycle.end);

  return {
    value: String(cycle.cyclesAgo),
    label: cycle.cyclesAgo === 0 ? `${range} (current)` : range,
  };
};

const UsageWorkersHeader = ({ usage, cyclesAgo, onCycleChange }: UsageWorkersHeaderProps) => {
  const selectedCycle = usage?.selectableCycles.find((cycle) => cycle.cyclesAgo === cyclesAgo);

  return (
    <>
      <UsageLayoutHeader />

      <PageInfoHeader
        title="Workers"
        caption={usage && selectedCycle && buildCycleCaption(usage, selectedCycle)}
      >
        {usage && usage.selectableCycles.length > 1 && (
          <Box className={styles.cycleSelect}>
            <Select
              aria-label={getCycleLabel(usage)}
              value={String(cyclesAgo)}
              items={usage.selectableCycles.map(getCycleOption)}
              onChange={(value) => onCycleChange(Number(value))}
            />
          </Box>
        )}
        <CSVDownloadDrawerButton
          aspectType="worker-count"
          initialRange={
            selectedCycle && getCSVDownloadRange(selectedCycle.start, selectedCycle.end)
          }
        />
      </PageInfoHeader>
    </>
  );
};

export default UsageWorkersHeader;
